import api from '../api';

export type TraWaitlistStatus = 'pending' | 'selected' | 'rejected' | 'waitlisted';

export interface TraWaitlistEntry {
  id: string;
  editionId: string | null;
  fullName: string;
  countryCode: string | null;
  stateProvince: string | null;
  city: string;
  educationalInstitution: string;
  degreeOrProgram: string;
  cedula: string;
  dateOfBirth: string;
  address: string;
  email: string;
  phone: string;
  rocketryExperience: string | null;
  motivation: string;
  referredBy: string | null;
  isExistingTraMember: boolean;
  traMemberNumber: string | null;
  status: TraWaitlistStatus;
  reviewNotes: string | null;
  reviewedById: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export type TraCertLevel = 'L1' | 'L2' | 'L3' | 'TMP';

export interface TraCertificationEdition {
  id: string;
  name: string;
  registrationStartDate: string;
  registrationDeadline: string;
  certLevels: TraCertLevel[];
  seedbedId: string | null;
  createdAt: string;
  updatedAt: string;
  waitlist_count?: number;
}

export interface TraMemberAdmin {
  id: string;
  fullName: string;
  traNumber: string | null;
  level: TraCertLevel;
  institutionName: string | null;
  city: string | null;
  certifiedAt: string | null;
  editionId: string | null;
  isPublic: boolean;
  edition: { id: string; name: string } | null;
  createdAt: string;
}

export interface TraWaitlistAgingEntry {
  waitlistEntryId: string;
  fullName: string;
  daysWaiting: number;
}

export interface TraWaitlistStats {
  waitlistAging: TraWaitlistAgingEntry[];
  unassignedWaitlistSize: number;
  nextEdition: { id: string; name: string; registrationDeadline: string } | null;
  netWaitlistGrowth: {
    periodStart: string;
    newEntries: number;
    assignedEntries: number;
    netGrowth: number;
  };
}

function data<T>(res: { data: { data: T } }): T {
  return res.data.data;
}

export const traAdmin = {
  listWaitlist: (params?: Record<string, string | number>) =>
    api
      .get<{ data: { items: TraWaitlistEntry[]; total: number; page: number; limit: number } }>(
        '/tra/certification-waitlist',
        { params },
      )
      .then(data),

  getWaitlistEntry: (id: string) =>
    api.get<{ data: TraWaitlistEntry }>(`/tra/certification-waitlist/${id}`).then(data),

  // gestion-role-based-stats-dashboards
  getWaitlistStats: () =>
    api.get<{ data: TraWaitlistStats }>('/tra/certification-waitlist/stats').then(data),

  updateWaitlistStatus: (id: string, body: { status: TraWaitlistStatus; reviewNotes?: string | null }) =>
    api.patch<{ data: TraWaitlistEntry }>(`/tra/certification-waitlist/${id}/status`, body).then(data),

  // tra-waitlist-edition-reassignment
  reassignWaitlistEdition: (id: string, editionId: string | null) =>
    api
      .patch<{ data: TraWaitlistEntry & { revokedPreviousEnrollment: boolean } }>(
        `/tra/certification-waitlist/${id}/edition`,
        { editionId },
      )
      .then(data),

  listEditions: (params?: Record<string, string | number>) =>
    api
      .get<{ data: { items: TraCertificationEdition[]; total: number; page: number; limit: number } }>(
        '/tra/certification-editions',
        { params },
      )
      .then(data),

  getActiveEditions: () =>
    api.get<{ data: TraCertificationEdition[] }>('/tra/certification-editions/active').then(data),

  getEdition: (id: string) =>
    api.get<{ data: TraCertificationEdition }>(`/tra/certification-editions/${id}`).then(data),

  createEdition: (body: {
    name: string;
    registrationStartDate: string;
    registrationDeadline: string;
    certLevels: TraCertLevel[];
    seedbedId?: string | null;
  }) => api.post<{ data: TraCertificationEdition }>('/tra/certification-editions', body).then(data),

  updateEdition: (
    id: string,
    body: Partial<{
      name: string;
      registrationStartDate: string;
      registrationDeadline: string;
      certLevels: TraCertLevel[];
      seedbedId: string | null;
    }>,
  ) => api.put<{ data: TraCertificationEdition }>(`/tra/certification-editions/${id}`, body).then(data),

  createWaitlistEntryAdmin: (body: {
    editionId: string;
    fullName: string;
    countryCode?: string | null;
    stateProvince?: string | null;
    city: string;
    educationalInstitution: string;
    degreeOrProgram: string;
    cedula: string;
    dateOfBirth: string;
    address: string;
    email: string;
    phone: string;
    rocketryExperience?: string | null;
    motivation: string;
    referredBy?: string | null;
    isExistingTraMember?: boolean;
    traMemberNumber?: string | null;
  }) => api.post<{ data: TraWaitlistEntry }>('/tra/certification-waitlist/admin', body).then(data),

  listMembers: (params?: Record<string, string | number>) =>
    api
      .get<{ data: { items: TraMemberAdmin[]; total: number; page: number; limit: number } }>(
        '/tra/members/admin',
        { params },
      )
      .then(data),

  getMember: (id: string) => api.get<{ data: TraMemberAdmin }>(`/tra/members/${id}/admin`).then(data),

  // tra-member-manual-certification
  updateMemberCertification: (
    id: string,
    body: { editionId?: string | null; certifiedAt?: string | null; traNumber?: string | null },
  ) => api.patch<{ data: TraMemberAdmin }>(`/tra/members/${id}/certification`, body).then(data),
};
